import {
    RiArrowRightSLine,
    RiCheckboxCircleFill, RiFileList2Line,
    RiGroup2Line,
    RiMailLine,
    RiSlackLine, RiStackLine, RiThumbUpLine, RiTimeLine, RiTimerFlashLine, RiUserAddLine,
    RiUserLine,
} from "@remixicon/react";
import React, {
    ReactElement, ReactNode,
    useMemo,
} from "react";
import { OnboardingProfile, useGetOnboardingProfileQuery } from "../../../../__generated__/api";
import { ReactComponent as ChannelGroupsIcon } from "../../../../assets/icons/groups-list.svg";
import { SuccessConfetti } from "../../../../components/confetti/Confetti";
import { ARTICLES } from "../../../../components/help/Help";
import Badge from "../../../../components/theme/badge/Badge";
import ButtonTabs from "../../../../components/theme/button/ButtonTabs";
import RouterLink from "../../../../components/theme/button/RouterLink";
import Icon from "../../../../components/theme/icon/Icon";
import ProgressBar from "../../../../components/theme/progress-bar/ProgressBar";
import Text from "../../../../components/theme/text/Text";
import useArticleContext from "../../../../contexts/HelpContext";
import useRouting, { NavLink, Paths, useParams } from "../../../../contexts/Routing";
import Footer from "./Footer";
import Styles from "./HomeV3.module.scss";

function LabelContainer({
    children,
    label,
}: {
    children: ReactNode;
    label: string
}) {
    return (
        <div className={Styles["label-container"]}>
            <Badge variant="gray" className={Styles["label-container-title"]}>{label}</Badge>
            {children}
        </div>
    );
}

function ActionButton({
    icon,
    label,
    isCompleted,
    setting,
    isOptional,
    isNew,
}: {
    icon: ReactNode;
    label: string;
    isCompleted: boolean;
    setting: string;
    isOptional?: boolean;
    isNew: boolean | undefined;
}) {
    // asdf
    return (
        <NavLink
            isButton
            variant="outline-gray"
            to={Paths.ADMINISTRATOR_SETTING}
            params={{ setting }}
        >
            <span className="btn-text">
                {icon}
                {" "}
                {label}
                {isOptional && <span className="btn-text-optional"> (Optional)</span>}
                {isNew && (
                    <Badge variant="counter" size="small" className={Styles["new-badge"]} isSelected>
                        New
                    </Badge>
                )}
            </span>
            {isCompleted ? (
                <RiCheckboxCircleFill className="btn-postfix-icon completed" />
            ) : (
                <RiArrowRightSLine className="btn-postfix-icon" />
            )}
        </NavLink>
    );
}
ActionButton.defaultProps = {
    isOptional: false,
};
export const STEPS = [
    {
        title: "Setup Portal Settings",
        description: "Customize your portal's preferences to fit your team's needs and workflows.",
        actions: [
            {
                title: "Add Users",
                setting: "users",
                icon: <RiUserAddLine />,
            },
            {
                title: "Add Groups",
                setting: "user-groups",
                icon: <RiGroup2Line />,
            },
            {
                title: "Artificial Intelligence",
                setting: "ai",
                isNew: true,
                isOptional: true,
                icon: <Icon.ArtificialIntelligence />,
            },
        ],
    },
    {
        title: "Setup Channels Settings",
        description: "Configure channels for smooth communication and ticket management.",
        actions: [
            {
                title: "Slack Settings",
                setting: "channel-settings",
                icon: <RiSlackLine />,
            },
            // {
            //     title: "Microsoft Teams Settings",
            //     setting: "microsoft-teams",
            //     icon: <MicrosoftTeamsLine />
            // },
            {
                title: "Email Settings",
                setting: "channels-emails",
                icon: <RiMailLine />,
            },
            {
                title: "Channel Groups",
                setting: "channel-tags",
                icon: <ChannelGroupsIcon />,
            },
        ],
    },
    {
        title: "Setup Workspace Settings",
        description: "Configure request types, tags, actions, and many more settings to optimize your workflow",
        actions: [
            {
                title: "Request Types",
                setting: "request-types",
                icon: <RiFileList2Line />,

            },
            {
                title: "Setup SLA's",
                setting: "sla",
                icon: <RiTimerFlashLine />,
                isOptional: true,
                isNew: false,
            },
            {
                title: "Add Office Hours",
                setting: "time",
                icon: <RiTimeLine />,
                isOptional: true,
            },
        ],
    },
];
const STEPS_FLATTENED = STEPS.flatMap(step => step.actions);

const COMMON_STEPS = [
    {
        id: "users",
        setting: "users",
        icon: <RiUserLine />,
        title: "Set up Users",
        description: "Add more agents or administrators.",

    },
    {
        id: "request-types",
        setting: "request-types",
        icon: <RiFileList2Line />,
        title: "Request Types",
        description: "Configure the types of requests users can create.",
    },
    {
        id: "time",
        setting: "time",
        icon: <RiTimeLine />,
        title: "Set up off-hours",
        description: "Set up off-hours and display a message when you are away.",

    },
    {
        id: "feedback",
        setting: "feedback",
        icon: <RiThumbUpLine />,
        title: "Set up Feedback",
        description: "Set the integrated Feedback.",

    },
    {
        id: "queues",
        setting: "queues",
        icon: <RiStackLine />,
        title: "Set up Queues",
        description: "Configure queues which manage ticket routing.",

    },
    {
        id: "services",
        setting: "workflow-services",
        icon: <Icon.Integrations />,
        title: "Integrations",
        description: "Configure integrations with your standard tools.",
    },

];

enum Pages {
    SETUP = "guide",
    COMMON = "common",
}

const TABS = [
    {
        text: "Setup Guide",
        key: Pages.SETUP,
    },
    {
        text: "Common Settings",
        key: Pages.COMMON,
    },
];

export function useProfile(profile?: OnboardingProfile | undefined) {
    return useMemo(() => {
        const homeStepsCompleted = new Set(profile?.homeStepsCompleted ?? []);

        const stepsCompleted = STEPS_FLATTENED.count(item => homeStepsCompleted.has(item.setting)) + 1;

        return {
            progressPercent: stepsCompleted / (STEPS_FLATTENED.length + 1),
            homeStepsCompleted,
            isDone: stepsCompleted === STEPS_FLATTENED.length + 1,
        };
    }, [profile]);
}

export default function Home(): ReactElement {
    const { historyPush, getSearchParams } = useRouting();

    const { data: onboardingData } = useGetOnboardingProfileQuery();
    const { progressPercent, homeStepsCompleted } = useProfile(onboardingData?.onboardingProfile as OnboardingProfile);
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);

    const selectedTab = id ?? (progressPercent >= 0.99 ? Pages.COMMON : Pages.SETUP);
    useArticleContext(selectedTab === Pages.SETUP ? ARTICLES.ONBOARDING_HOME_PAGE : ARTICLES.HOME);

    const searchParams = getSearchParams(Paths.HOME);
    const { isOnboarded } = searchParams;

    return (
        <div>
            <div className={Styles.header}>
                <h1>
                    Settings
                </h1>
                {isOnboarded && <SuccessConfetti timeout={5000} />}

                <ButtonTabs
                    values={TABS}
                    value={selectedTab}
                    className={Styles["tabs-container"]}
                    onChange={value => historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                        id: value.key,
                        setting: "home",
                    })}
                />
                {selectedTab === Pages.SETUP && (
                    <div>
                        <p>
                            It will help you set up the system. Follow the steps to create a seamless setup experience.
                        </p>

                        <ProgressBar value={Math.min(progressPercent, 1)} variant="progress" shouldShowLabel />
                    </div>
                )}
                {selectedTab === Pages.COMMON && (
                    <div>
                        <p>
                            These are common settings to adjust, but you can view all settings by
                            exploring the categories on the left
                        </p>
                    </div>
                )}

            </div>

            {selectedTab === Pages.SETUP && (
                <div className={Styles.content}>
                    {STEPS.map((section, index) => (
                        <LabelContainer label={`Step ${index + 1}`}>
                            <h2>{section.title}</h2>
                            <p>{section.description}</p>
                            <div className={Styles["list-actions"]}>
                                {section.actions.map(action => (
                                    <ActionButton
                                        setting={action.setting}
                                        icon={action.icon}
                                        isCompleted={homeStepsCompleted.has(action.setting)}
                                        label={action.title}
                                        isOptional={action.isOptional}
                                        isNew={action.isNew}
                                    />
                                ))}
                            </div>
                        </LabelContainer>
                    ))}
                </div>
            )}
            {selectedTab === Pages.COMMON && (
                <div className={Styles["common-steps"]}>
                    {COMMON_STEPS.map(section => (
                        <RouterLink
                            to={Paths.ADMINISTRATOR_SETTING}
                            params={{ setting: section.setting }}
                            variant="none"
                            className={Styles["common-step"]}
                            key={section.id}
                        >
                            <Icon background="primary" isRounded size="xlarge" variant="primary" icon={section.icon} />
                            <Text textVariant="body-1-semibold" colorVariant="text-title">{section.title}</Text>
                            <Text textVariant="body-2-regular" colorVariant="body-secondary">{section.description}</Text>
                            <Text textVariant="body-2-medium" colorVariant="text-link">
                                Configure
                                {" "}
                                <Icon.Next />
                            </Text>

                        </RouterLink>
                    ))}
                </div>
            )}

            <Footer />

        </div>

    );
}
